import { useTranslation } from "react-i18next";
import { footerLinks, contactInfo } from "@/mocks/homeData";
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface FooterProps {
  onOpenModal: (type: string) => void;
}

export default function Footer({ onOpenModal }: FooterProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    link: { labelKey: string; href?: string; action?: string; modalType?: string }
  ) => {
    if (link.action === "external" && link.href) {
      if (!link.href.startsWith("http")) {
        e.preventDefault();
        navigate(link.href);
      }
      return;
    }
    if (link.action === "scroll" && link.href) {
      e.preventDefault();
      const el = document.querySelector(link.href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (link.action === "modal" && link.modalType) {
      e.preventDefault();
      onOpenModal(link.modalType);
      return;
    }
    if (link.href && link.href.startsWith("http")) return;
    e.preventDefault();
  };

  const renderLinkList = (
    links: Array<{ labelKey: string; href?: string; action?: string; modalType?: string }>
  ) => (
    <ul className="space-y-1.5">
      {links.map((link) => (
        <li key={link.labelKey}>
          <a
            href={link.href || "#"}
            onClick={(e) => handleLinkClick(e, link)}
            className="text-[12px] text-foreground-300/70 hover:text-primary-300 transition-colors duration-300 font-body tracking-wide cursor-pointer inline-flex items-center gap-1.5 group whitespace-nowrap"
          >
            <span className="w-1 h-1 rounded-full bg-primary-400/0 group-hover:bg-primary-400/60 transition-colors flex-shrink-0" />
            {t(link.labelKey)}
          </a>
        </li>
      ))}
    </ul>
  );

  const socials = [
    { icon: "ri-linkedin-fill", href: contactInfo.social.linkedin, label: "LinkedIn" },
    { icon: "ri-facebook-fill", href: contactInfo.social.facebook, label: "Facebook" },
    { icon: "ri-instagram-fill", href: contactInfo.social.instagram, label: "Instagram" },
    { icon: "ri-youtube-fill", href: contactInfo.social.youtube, label: "YouTube" },
    { icon: "ri-tiktok-fill", href: contactInfo.social.tiktok, label: "TikTok" },
  ];

  return (
    <footer
      ref={sectionRef}
      className={`relative bg-[#0F1C2E] transition-all duration-1000 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {/* Top neon line */}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-[#4A7DBF]/50 to-transparent" />

      {/* Floating horizontal social bar */}
      <div className="hidden md:flex absolute -top-5 right-6 lg:right-12 z-20 items-center gap-1 pl-4 pr-2 py-1.5 rounded-full bg-[#0F1C2E] border border-white/10">
        <span className="font-label text-[11px] uppercase tracking-widest text-foreground-200/60 mr-2 whitespace-nowrap">
          {t("footer.followUs")}
        </span>
        {socials.map((social) => (
          <a
            key={social.label}
            href={social.href}
            target="_blank"
            rel="noopener noreferrer"
            className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-300/60 hover:text-white hover:bg-[#4A7DBF]/25 transition-all duration-300 cursor-pointer"
            aria-label={social.label}
          >
            <i className={`${social.icon} text-sm`} />
          </a>
        ))}
      </div>

      {/* Main footer content */}
      <div className="w-full px-6 sm:px-8 lg:px-12 pt-10 md:pt-12 pb-7">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-7 md:gap-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-3">
              <div className="flex items-center gap-2 mb-3">
                <img
                  src="https://storage.helloreaddy.io/project_files/b2c15536-355d-426f-a10e-89e210e7256f/284a1860-12a0-47a5-b9c0-f19215b3f5a7_compressed_icono4x.webp"
                  alt="Openlab Secure"
                  className="h-6 w-auto"
                  decoding="async"
                />
                <span className="font-heading font-semibold text-sm text-white tracking-wide">
                  Openlab Secure
                </span>
              </div>
              <p className="text-[12px] text-foreground-300/55 font-body max-w-[240px] leading-relaxed mb-3">
                {t("footer.tagline")}
              </p>
              <a
                href={`mailto:${contactInfo.email}`}
                className="inline-flex items-center gap-2 text-[12px] text-foreground-300/70 font-body hover:text-primary-300 transition-colors cursor-pointer"
              >
                <i className="ri-mail-line text-[#4A7DBF]/70 text-sm" />
                {contactInfo.email}
              </a>

              {/* Mobile socials - horizontal */}
              <div className="md:hidden mt-4">
                <span className="font-label text-[11px] uppercase tracking-widest text-foreground-200/60">
                  {t("footer.followUs")}
                </span>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {socials.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-foreground-300/60 hover:text-white hover:border-[#4A7DBF]/40 hover:bg-[#4A7DBF]/15 transition-all duration-300 cursor-pointer"
                      aria-label={social.label}
                    >
                      <i className={`${social.icon} text-sm`} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Acerca de */}
            <div className="md:col-span-3">
              <h6 className="font-heading font-semibold text-[13px] text-white mb-3 tracking-wide">
                {t("footer.about")}
              </h6>
              {renderLinkList(footerLinks.about)}
            </div>

            {/* Contratacion y Ventas */}
            <div className="md:col-span-3">
              <h6 className="font-heading font-semibold text-[13px] text-white mb-3 tracking-wide">
                {t("footer.sales")}
              </h6>
              {renderLinkList(footerLinks.sales)}
            </div>

            {/* Contacto */}
            <div className="md:col-span-3">
              <h6 className="font-heading font-semibold text-[13px] text-white mb-3 tracking-wide">
                {t("footer.contact")}
              </h6>
              {renderLinkList(footerLinks.contact)}
              <a
                href={`mailto:${contactInfo.email}`}
                className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-foreground-300/60 font-body hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-[#4A7DBF]/60 text-xs" />
                {contactInfo.email}
              </a>
              <a
                href={contactInfo.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-foreground-300/60 font-body hover:text-[#25D366] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-[#25D366]/70 text-xs" />
                {contactInfo.phone}
              </a>
            </div>
          </div>

          {/* Legal links - compact wrapped row */}
          <div className="border-t border-white/6 mt-7 pt-4">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
              {footerLinks.legal.map((link) => (
                <a
                  key={link.labelKey}
                  href="#"
                  onClick={(e) => handleLinkClick(e, link)}
                  className="text-[11px] text-foreground-400/50 hover:text-primary-300 transition-colors duration-300 font-body tracking-wide cursor-pointer whitespace-nowrap"
                >
                  {t(link.labelKey)}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="w-full px-6 sm:px-8 lg:px-12 py-3">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <img
                src="https://storage.helloreaddy.io/project_files/b2c15536-355d-426f-a10e-89e210e7256f/284a1860-12a0-47a5-b9c0-f19215b3f5a7_compressed_icono4x.webp"
                alt="Openlab Secure"
                className="h-5 w-auto"
                decoding="async"
              />
              <span className="text-[12px] text-foreground-400/50 font-body">
                Openlab Secure &copy; {new Date().getFullYear()} &mdash; {t("footer.rights")}
              </span>
            </div>
            <p className="text-[11px] text-foreground-400/40">
              {t("footer.tagline")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}